import { useState } from 'react'
import { pauseAgent, resumeAgent, terminateAgent, forceCheckpoint } from '../api/vestige.js'

function ConfirmDialog({ message, onConfirm, onCancel, loading }) {
  return (
    <div className="mt-3 p-3 border border-red/50 rounded-lg bg-red-faint">
      <p className="text-xs text-text-dim mb-3">{message}</p>
      <div className="flex gap-2 justify-end">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-xs text-text-dim border border-border rounded hover:border-border-bright transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="px-3 py-1.5 text-xs font-medium text-red border border-red rounded hover:bg-red/10 transition-colors disabled:opacity-50"
        >
          {loading ? 'Working…' : 'Confirm'}
        </button>
      </div>
    </div>
  )
}

function ControlButton({ onClick, disabled, loading, variant = 'default', children, className = '' }) {
  const base = 'px-4 py-2 text-xs font-mono font-medium rounded border transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
  const variants = {
    default: 'text-text-dim border-border hover:border-border-bright hover:text-text',
    accent: 'text-accent border-accent hover:bg-accent-faint',
    danger: 'text-red border-red hover:bg-red-faint',
    green: 'text-green border-green hover:bg-green-faint',
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`${base} ${variants[variant]} ${className}`}
    >
      {loading ? 'Working…' : children}
    </button>
  )
}

export default function AgentOverridePanel({ workspace, onWorkspaceUpdate }) {
  const [action, setAction] = useState(null) // 'pause' | 'resume' | 'checkpoint' | 'terminate'
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState(null)

  const status = workspace?.status
  const workspaceId = workspace?.id

  const execute = async (fn, next) => {
    setAction(next)
    setError(null)
    setConfirming(false)
    try {
      const result = await fn(workspaceId)
      if (onWorkspaceUpdate) onWorkspaceUpdate(result)
    } catch (err) {
      setError(err.message)
    } finally {
      setAction(null)
    }
  }

  const handleCheckpoint = () => execute(forceCheckpoint, 'checkpoint')
  const handlePause = () => execute(pauseAgent, 'pause')
  const handleResume = () => execute(resumeAgent, 'resume')
  const handleTerminate = () => execute(terminateAgent, 'terminate')

  return (
    <div className="border border-border rounded-lg bg-surface overflow-hidden">
      <div className="flex items-center px-4 py-3 border-b border-border bg-surface2">
        <span className="text-2xs font-mono text-text-faint tracking-widest uppercase">
          Agent control
        </span>
      </div>

      <div className="p-4 flex flex-col gap-3">
        {error && (
          <div className="rounded px-3 py-2 bg-red-faint border border-red/50" role="alert">
            <p className="text-xs text-red">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          {status === 'ACTIVE' ? (
            <ControlButton
              variant="default"
              loading={action === 'pause'}
              onClick={handlePause}
            >
              Pause agent
            </ControlButton>
          ) : status === 'PAUSED' ? (
            <ControlButton
              variant="green"
              loading={action === 'resume'}
              onClick={handleResume}
            >
              Resume agent
            </ControlButton>
          ) : (
            <ControlButton variant="default" disabled>
              {status === 'TERMINATED' ? 'Terminated' : 'Unavailable'}
            </ControlButton>
          )}

          <ControlButton
            variant="accent"
            loading={action === 'checkpoint'}
            disabled={status === 'TERMINATED'}
            onClick={handleCheckpoint}
          >
            Force checkpoint
          </ControlButton>
        </div>

        <div className="border-t border-border pt-3">
          {!confirming ? (
            <ControlButton
              variant="danger"
              disabled={status === 'TERMINATED'}
              className="w-full justify-center"
              onClick={() => setConfirming(true)}
            >
              Terminate agent
            </ControlButton>
          ) : (
            <ConfirmDialog
              message="This will permanently stop the agent. Its last state is preserved as a snapshot. This cannot be undone."
              onConfirm={handleTerminate}
              onCancel={() => setConfirming(false)}
              loading={action === 'terminate'}
            />
          )}
        </div>

        <p className="text-2xs font-mono text-text-faint leading-relaxed">
          Terminate stops the agent process. Rollback reverts workspace state to a prior snapshot.
        </p>
      </div>
    </div>
  )
}
